import { GoogleGenAI, Modality } from "@google/genai";

export const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

const base64ToGenerativePart = (base64, mimeType) => {
  return {
    inlineData: {
      data: base64.split(',')[1],
      mimeType,
    },
  };
};

const getAi = () => {
  const apiKey = process.env.REACT_APP_API_KEY;
  if (!apiKey) {
    throw new Error("REACT_APP_API_KEY environment variable not set");
  }
  return new GoogleGenAI({ apiKey: apiKey,
    baseUrl: "https://us-central1-aiplatform.googleapis.com/v1" });
}

export const generateProPhotos = async (base64Images, count, prompt) => {
  const ai = getAi();
  
  const imageParts = base64Images.map(image => 
    base64ToGenerativePart(image.base64, image.mimeType)
  );

  const promises = Array.from({ length: count }).map(async () => {
    const response = await ai.models.generateContent({
      model: 'gemini-2.5-flash-image',
      contents: {
        parts: [
          ...imageParts,
          { text: `Reference images provided. ${prompt}` },
        ],
      },
      config: {
        responseModalities: [Modality.IMAGE],
      },
    });

    const part = response.candidates?.[0]?.content?.parts?.[0];
    if (part?.inlineData) {
      const { data, mimeType } = part.inlineData;
      return {
        id: crypto.randomUUID(),
        base64: `data:${mimeType};base64,${data}`,
        mimeType: mimeType,
      };
    }
    throw new Error('Image generation failed to return valid data.');
  });
  return Promise.all(promises);
};



export const editImageWithPrompt = async (image, prompt) => {
  const ai = getAi();
  const imagePart = base64ToGenerativePart(image.base64, image.mimeType);
  
  const response = await ai.models.generateContent({
    model: 'gemini-2.5-flash-image',
    contents: {
      parts: [imagePart, { text: prompt }],
    },
    config: {
      responseModalities: [Modality.IMAGE],
    },
  });

  const part = response.candidates?.[0]?.content?.parts?.[0];
  if (part?.inlineData) {
    const { data, mimeType } = part.inlineData;
    return {
      id: image.id,
      base64: `data:${mimeType};base64,${data}`,
      mimeType: mimeType,
    };
  }
  throw new Error('Image editing failed.');
};

// ========== FIXED VIDEO GENERATION FUNCTIONS ==========

export const generateProductVideo = async (uploadedFile, aspectRatio, prompt) => {
  const ai = getAi();
  console.log("🎬 Starting video generation...");
  
  const config = {
    aspectRatio: aspectRatio,
    durationSeconds: 8,
    resolution: "720p",
    personGeneration: "allow_adult"
  };

  try {
    let operation;
    
    if (uploadedFile && uploadedFile.base64) {
      const imageBytes = uploadedFile.base64.split(',')[1];
      
      operation = await ai.models.generateVideos({
        model: "veo-3.1-lite-generate-preview",
        prompt: prompt,
        image: {
          imageBytes: imageBytes,
          mimeType: uploadedFile.mimeType || "image/jpeg"
        },
        config: config
      });
    } else {
      operation = await ai.models.generateVideos({
        model: "veo-3.1-lite-generate-preview",
        prompt: prompt,
        config: config
      });
    }
    
    console.log("✅ Operation started:", operation);
    return operation;
    
  } catch (error) {
    console.error("❌ Veo API Error:", error);
    throw error;
  }
};

export const pollVideoStatus = async (operation) => {
  const ai = getAi();
  console.log("📊 Polling video status...");
  
  const result = await ai.operations.getVideosOperation({
    operation: operation
  });
  
  console.log("📊 Poll result:", result);
  
  // Check if operation is complete
  if (result.done && result.response?.generateVideoResponse?.generatedSamples?.length > 0) {
    const video = result.response.generateVideoResponse.generatedSamples[0];
    const videoUri = video.video?.uri;
    
    if (videoUri) {
      const apiKey = process.env.REACT_APP_API_KEY;
      
      // ✅ Just add the API key to the URL - NO downloading needed!
      const videoUrlWithKey = `${videoUri}&key=${apiKey}`;
      
      console.log("🎬 Video URL with key:", videoUrlWithKey);
      
      return {
        done: true,
        videoUrl: videoUrlWithKey  // Return the URL directly
      };
    }
  }
  
  return result;
};